import { makeBooleanDataField, makeInt64DataField, makeStringDataField } from "@microsoft/oteljs";
import { TraceLevel } from "../events/Trace.event";
/**
 * Maximum allowed payload size in bytes for summary events.
 * Set conservatively below the OneCollector server limit (~2-4MB) to ensure delivery.
 */ export const MaxSummaryPayloadSizeBytes = 50000; // 50KB
/**
 * Maximum length for truncated string fields in summary events.
 */ export const MaxTruncatedStringLength = 200;
/**
 * Reserved bytes for summary metadata (event name, discard reason, size, timestamp).
 */ const MetadataBytesOverhead = 500;
/**
 * Estimated bytes per field for JSON structure overhead (quotes, colons, commas).
 */ const JsonStructureOverhead = 10;
/**
 * Maximum size of the handled event keys set to prevent memory bloat.
 */ const MaxHandledEventKeys = 1000;
/**
 * Events discarded reason enum values (from @microsoft/applicationinsights-core-js)
 * Using numeric values to avoid import dependency issues
 */ export const EventsDiscardedReasonValues = {
    Unknown: 0,
    NonRetryableStatus: 1,
    InvalidEvent: 2,
    SizeLimitExceeded: 3,
    KillSwitch: 4,
    QueueFull: 5
};
/**
 * Creates a truncated summary from an oversized telemetry event.
 * Truncates large string fields and extracts key information to create a smaller,
 * deliverable version of the event.
 *
 * @param event - The original oversized telemetry event
 * @param reason - The reason the event was discarded
 * @param maxStringLength - Maximum length for truncated strings
 * @returns A truncated summary of the event
 */ export function createTruncatedSummary(event, reason, maxStringLength = MaxTruncatedStringLength) {
    const eventName = event.name;
    const originalSize = estimateEventSize(event);
    // Extract and truncate key data fields
    const truncatedData = {};
    const data = event.data || {};
    // Keep track of truncated data size
    let currentSize = 0;
    const maxDataSize = MaxSummaryPayloadSizeBytes - MetadataBytesOverhead;
    // Priority substrings to look for in field names (case-insensitive contains check)
    // Fields containing these strings are considered important for diagnostics
    // See: Qos.event.ts, Action.event.ts, Api.event.ts for field definitions
    const prioritySubstrings = [
        "Action_Name",
        "ResultCode",
        "ResultType",
        "ErrorMessage",
        "ErrorCode"
    ];
    // Helper to check if a field name contains any priority substring
    const isPriorityField = (fieldName)=>{
        const lowerFieldName = fieldName.toLowerCase();
        return prioritySubstrings.some((substr)=>lowerFieldName.includes(substr.toLowerCase()));
    };
    // Separate fields into priority and non-priority
    const allFields = Object.entries(data);
    const priorityFields = allFields.filter(([key])=>isPriorityField(key));
    const otherFields = allFields.filter(([key])=>!isPriorityField(key));
    // First, add priority fields
    for (const [key, value] of priorityFields){
        if (currentSize >= maxDataSize) break;
        const truncatedValue = truncateValue(value, maxStringLength);
        const fieldSize = estimateFieldSize(key, truncatedValue);
        if (currentSize + fieldSize <= maxDataSize) {
            truncatedData[key] = truncatedValue;
            currentSize += fieldSize;
        }
    }
    // Then add other fields until we hit the size limit
    for (const [key, value] of otherFields){
        if (currentSize >= maxDataSize) break;
        const truncatedValue = truncateValue(value, maxStringLength);
        const fieldSize = estimateFieldSize(key, truncatedValue);
        if (currentSize + fieldSize <= maxDataSize) {
            truncatedData[key] = truncatedValue;
            currentSize += fieldSize;
        }
    }
    return {
        originalEventName: eventName,
        discardReason: getDiscardReasonName(reason),
        originalSizeBytes: originalSize,
        truncatedData,
        originalTimestamp: event.time ? new Date(event.time).getTime() : Date.now()
    };
}
/**
 * Converts a truncated summary to an array of DataFields for telemetry
 */ export function summaryToDataFields(summary) {
    const fields = [
        makeStringDataField("TruncatedEvent_OriginalEventName", summary.originalEventName),
        makeStringDataField("TruncatedEvent_DiscardReason", summary.discardReason),
        makeInt64DataField("TruncatedEvent_OriginalSizeBytes", summary.originalSizeBytes),
        makeBooleanDataField("TruncatedEvent_IsTruncated", true)
    ];
    if (summary.originalTimestamp) {
        fields.push(makeInt64DataField("TruncatedEvent_OriginalTimestamp", summary.originalTimestamp));
    }
    // Add truncated data fields with a prefix to distinguish from original data
    for (const [key, value] of Object.entries(summary.truncatedData)){
        const fieldName = `TruncatedEvent_Data_${key}`;
        if (typeof value === "string") {
            fields.push(makeStringDataField(fieldName, value));
        } else if (typeof value === "number") {
            fields.push(makeInt64DataField(fieldName, value));
        } else if (typeof value === "boolean") {
            fields.push(makeBooleanDataField(fieldName, value));
        }
    }
    return fields;
}
/**
 * Truncates a value to a maximum string length
 */ function truncateValue(value, maxLength) {
    if (typeof value === "boolean") {
        return value;
    }
    if (typeof value === "number") {
        return value;
    }
    if (typeof value === "string") {
        if (value.length > maxLength) {
            return value.substring(0, maxLength) + "...[truncated]";
        }
        return value;
    }
    // For objects/arrays, stringify and truncate
    try {
        const stringified = JSON.stringify(value);
        if (stringified.length > maxLength) {
            return stringified.substring(0, maxLength) + "...[truncated]";
        }
        return stringified;
    } catch  {
        return "[unserializable]";
    }
}
/**
 * Estimates the size of a telemetry event in bytes.
 * Uses TextEncoder for accurate UTF-8 byte counting when available (handles non-ASCII characters correctly).
 * @returns Size in bytes, or -1 if serialization fails
 */ function estimateEventSize(event) {
    try {
        return new TextEncoder().encode(JSON.stringify(event)).length;
    } catch  {
        return -1;
    }
}
/**
 * Estimates the size of a summary in bytes.
 * Uses TextEncoder for accurate UTF-8 byte counting when available (handles non-ASCII characters correctly).
 * @returns Size in bytes, or -1 if serialization fails
 */ function estimateSummarySize(summary) {
    try {
        return new TextEncoder().encode(JSON.stringify(summary)).length;
    } catch  {
        return -1;
    }
}
/**
 * Estimates the size of a single field in bytes
 */ function estimateFieldSize(key, value) {
    return key.length + String(value).length + JsonStructureOverhead;
}
/**
 * Converts a numeric discard reason to a human-readable string
 */ function getDiscardReasonName(reason) {
    switch(reason){
        case EventsDiscardedReasonValues.Unknown:
            return "Unknown";
        case EventsDiscardedReasonValues.NonRetryableStatus:
            return "NonRetryableStatus_413";
        case EventsDiscardedReasonValues.InvalidEvent:
            return "InvalidEvent";
        case EventsDiscardedReasonValues.SizeLimitExceeded:
            return "SizeLimitExceeded";
        case EventsDiscardedReasonValues.KillSwitch:
            return "KillSwitch";
        case EventsDiscardedReasonValues.QueueFull:
            return "QueueFull";
        default:
            return `Unknown(${reason})`;
    }
}
/**
 * Creates a notification listener that handles oversized events (413 errors).
 *
 * When a telemetry payload is too large and gets rejected with 413 Content Too Large,
 * this handler creates a truncated summary of the event and sends it instead,
 * preserving key diagnostic information.
 *
 * @param options - Configuration options for the handler
 * @returns An INotificationListener to be passed to OneDSSink
 *
 * @example
 * ```typescript
 * const oversizedHandler = createOversizedEventHandler({
 *     sendSummaryEvent: (eventName, dataFields) => {
 *         sink.sendTelemetryEvent({ eventName, dataFields, eventFlags });
 *     },
 * });
 *
 * const sink = new OneDSSink(persistentDataFields, {
 *     ...otherOptions,
 *     notificationListener: oversizedHandler,
 * });
 * ```
 */ export function createOversizedEventHandler(options) {
    const { sendSummaryEvent, maxSummaryPayloadSize = MaxSummaryPayloadSizeBytes, maxTruncatedStringLength = MaxTruncatedStringLength, traceLogger } = options;
    // Track already-handled events to prevent infinite loops
    const handledEventKeys = new Set();
    return {
        /**
         * Called when events are discarded by the SDK.
         * For oversized events (413), creates and sends a truncated summary.
         */ eventsDiscarded: (events, reason)=>{
            if (!events || events.length === 0) {
                return;
            }
            // Only handle size-related issues (SizeLimitExceeded or NonRetryableStatus which includes 413)
            const isOversizedError = reason === EventsDiscardedReasonValues.SizeLimitExceeded || reason === EventsDiscardedReasonValues.NonRetryableStatus;
            if (!isOversizedError) {
                return;
            }
            traceLogger?.logTrace(0x1e05a290 /* tag_4b0kq */ , TraceLevel.Warning, `Oversized telemetry detected [EventCount=${events.length}][Reason=${getDiscardReasonName(reason)}]`);
            // Process each discarded event
            let totalSummarySize = 0;
            const summaries = [];
            for (const event of events){
                const eventKey = `${event.name}_${event.time}`;
                // Skip if we've already handled this event (prevent infinite loops)
                if (handledEventKeys.has(eventKey)) {
                    continue;
                }
                const summary = createTruncatedSummary(event, reason, maxTruncatedStringLength);
                const summarySize = estimateSummarySize(summary);
                // Check if adding this summary would exceed our batch limit
                if (totalSummarySize + summarySize > maxSummaryPayloadSize) {
                    break;
                }
                summaries.push(summary);
                totalSummarySize += summarySize;
                handledEventKeys.add(eventKey);
                // Limit the set size to prevent memory bloat
                if (handledEventKeys.size > MaxHandledEventKeys) {
                    const firstKey = handledEventKeys.values().next().value;
                    if (firstKey) {
                        handledEventKeys.delete(firstKey);
                    }
                }
            }
            // Send truncated summary events
            for (const summary of summaries){
                try {
                    const dataFields = summaryToDataFields(summary);
                    sendSummaryEvent("TelemetryOversizedEvent", dataFields);
                } catch  {
                    traceLogger?.logTrace(0x1e05a28f /* tag_4b0kp */ , TraceLevel.Warning, `Failed to send truncated summary [EventName=${summary.originalEventName}][OriginalSize=${summary.originalSizeBytes}]`);
                }
            }
        },
        /**
         * Called when events are successfully sent
         */ eventsSent: (_events)=>{
        // No-op: we don't need to track successful sends
        }
    };
}
